<template>
  <div class="policy-list">
    <div class="page-header">
      <h2>会计决策策略</h2>
      <div class="header-actions">
        <el-select v-model="statusFilter" placeholder="状态筛选" size="small" @change="loadData" style="width:120px;margin-right:8px;">
          <el-option label="全部" value="" />
          <el-option label="草稿" value="DRAFT" />
          <el-option label="已发布" value="ACTIVE" />
          <el-option label="已归档" value="ARCHIVED" />
        </el-select>
        <el-button type="primary" size="small" @click="showCreateDialog = true">新建策略</el-button>
      </div>
    </div>

    <div v-if="!currentAccountSets || !currentAccountSets.id" class="no-account-tip">
      <el-alert title="请先选择账套" type="warning" :closable="false" description="需要选择一个账套才能管理会计决策策略" show-icon />
    </div>

    <el-table :data="policySets" v-loading="loading" stripe style="width:100%" v-else>
      <el-table-column prop="policyName" label="策略名称" min-width="160" />
      <el-table-column prop="policyCode" label="策略编码" width="220" />
      <el-table-column prop="businessScene" label="业务场景" width="140">
        <template slot-scope="scope">
          <el-tag :type="sceneTag(scope.row.businessScene)" size="small">{{ sceneLabel(scope.row.businessScene) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="statusTag(scope.row.status)" size="small">{{ statusLabel(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="activeVersion" label="当前版本" width="90" align="center">
        <template slot-scope="scope">v{{ scope.row.activeVersion || '-' }}</template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="更新时间" width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="280" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="text" @click="viewDetail(scope.row)">详情</el-button>
          <el-button size="mini" type="text" @click="publishPolicy(scope.row)" :disabled="scope.row.status !== 'DRAFT'">发布</el-button>
          <el-button size="mini" type="text" style="color:#f56c6c" @click="archivePolicy(scope.row)" :disabled="scope.row.status === 'ARCHIVED'">归档</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="新建策略集" :visible.sync="showCreateDialog" width="500px">
      <el-form label-width="120px">
        <el-form-item label="策略编码">
          <el-input v-model="newSet.policyCode" placeholder="例: PURCHASE_PAYMENT_POLICY" />
        </el-form-item>
        <el-form-item label="策略名称">
          <el-input v-model="newSet.policyName" />
        </el-form-item>
        <el-form-item label="业务场景">
          <el-select v-model="newSet.businessScene" style="width:100%">
            <el-option label="采购付款" value="PURCHASE_PAYMENT" />
            <el-option label="物流付款" value="LOGISTICS_PAYMENT" />
            <el-option label="银行流水" value="BANK_FLOW" />
          </el-select>
        </el-form-item>
        <el-form-item label="描述">
          <el-input type="textarea" v-model="newSet.description" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showCreateDialog = false">取消</el-button>
        <el-button type="primary" @click="createSet">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../api/accountingPolicy';
import {mapState} from 'vuex';

export default {
  computed: {
    ...mapState(['currentAccountSets'])
  },
  data() {
    return {
      policySets: [],
      loading: false,
      statusFilter: '',
      showCreateDialog: false,
      newSet: {
        accountSetsId: null,
        policyCode: '',
        policyName: '',
        businessScene: 'PURCHASE_PAYMENT',
        description: ''
      }
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      if (!this.currentAccountSets || !this.currentAccountSets.id) {
        this.loading = false;
        return;
      }
      this.loading = true;
      api.listPolicySets({ accountSetsId: this.currentAccountSets.id, status: this.statusFilter || undefined })
        .then(res => { this.policySets = res.data || []; })
        .catch(() => { this.$message.error('加载策略列表失败'); })
        .finally(() => { this.loading = false; });
    },
    viewDetail(row) {
      this.$router.push(`/accounting-policy/${row.id}`);
    },
    publishPolicy(row) {
      this.$confirm('确认发布该策略？发布后将生成版本快照。', '提示').then(() => {
        api.publishPolicy(row.id, { operatorId: 1 }).then(() => {
          this.$message.success('发布成功');
          this.loadData();
        });
      }).catch(() => {});
    },
    archivePolicy(row) {
      this.$confirm('确认归档该策略？归档后无法修改。', '提示').then(() => {
        api.archivePolicy(row.id, { remark: '手动归档' }).then(() => {
          this.$message.success('归档成功');
          this.loadData();
        });
      }).catch(() => {});
    },
    createSet() {
      this.newSet.accountSetsId = this.currentAccountSets ? this.currentAccountSets.id : null;
      if (!this.newSet.accountSetsId) {
        this.$message.warning('请先选择账套');
        return;
      }
      api.createPolicySet(this.newSet).then(() => {
        this.$message.success('创建成功');
        this.showCreateDialog = false;
        this.loadData();
      }).catch(() => { this.$message.error('创建失败'); });
    },
    sceneTag(s) { return s === 'PURCHASE_PAYMENT' ? '' : s === 'LOGISTICS_PAYMENT' ? 'warning' : 'info'; },
    sceneLabel(s) { return { PURCHASE_PAYMENT: '采购付款', LOGISTICS_PAYMENT: '物流付款', BANK_FLOW: '银行流水' }[s] || s; },
    statusTag(s) { return s === 'ACTIVE' ? 'success' : s === 'DRAFT' ? 'info' : 'danger'; },
    statusLabel(s) { return { ACTIVE: '已发布', DRAFT: '草稿', ARCHIVED: '已归档' }[s] || s; },
    dateFormatter(row, col, val) { return val ? val.substring(0, 19).replace('T', ' ') : ''; }
  }
};
</script>

<style scoped>
.policy-list { padding: 20px; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.header-actions { display: flex; align-items: center; }
.no-account-tip { margin: 20px 0; }
</style>
